import * as React from 'react';
import {useEffect, useState} from 'react';
import GlobalStyles from '@mui/joy/GlobalStyles';
import Avatar from '@mui/joy/Avatar';
import Box from '@mui/joy/Box';
import Divider from '@mui/joy/Divider';
import IconButton from '@mui/joy/IconButton';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton, {listItemButtonClasses} from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import Typography from '@mui/joy/Typography';
import Sheet from '@mui/joy/Sheet';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import {closeSidebar} from './utils';
import {NavigateFunction, NavLink, useNavigate} from 'react-router-dom';
import * as AuthService from "../../services/auth.service";
import EventBus from "./EventBus";
import IUser from "../../types/user.type";
import logo from "../../assets/FyG-lupa.png"
import AssuredWorkloadIcon from '@mui/icons-material/AssuredWorkload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import BadgeIcon from '@mui/icons-material/Badge';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import {PermissionType} from "../../types/enumeration/PermissionType";
import SettingsIcon from '@mui/icons-material/Settings';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CategoryIcon from '@mui/icons-material/Category';
import HandymanIcon from '@mui/icons-material/Handyman';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GradingIcon from '@mui/icons-material/Grading';

function Toggler({defaultExpanded = false, renderToggle, children}: {
  defaultExpanded?: boolean;
  children: React.ReactNode;
  renderToggle: (params: {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  }) => React.ReactNode;
}) {
  const [open, setOpen] = React.useState(defaultExpanded);

  return (
    <React.Fragment>
      {renderToggle({ open, setOpen })}
      <Box
        sx={[
          {
            display: 'grid',
            transition: '0.2s ease',
            '& > *': {
              overflow: 'hidden',
            },
          },
          open ? { gridTemplateRows: '1fr' } : { gridTemplateRows: '0fr' },
        ]}
      >
        {children}
      </Box>
    </React.Fragment>
  );
}

export default function Sidebar() {
  let navigate: NavigateFunction = useNavigate();
  const [currentUser, setCurrentUser] = useState<IUser | undefined>(undefined);

  const getOpen = (sections: Array<string>) => {
    let uri = window.location.pathname;
    let section = uri.split( '/')[1];
    return sections.includes(section);
  };

  useEffect(() => {
    const user = AuthService.getCurrentUser();
    if (user) {
      setCurrentUser(user);
    }
  }, []);

  const logOut = () => {
    AuthService.logout();
    EventBus.dispatch("logout");
    setCurrentUser(undefined);
    navigate('/login');
  };

  const hasRole = (role: PermissionType): boolean => {
    return currentUser?.roles?.includes(role) as boolean;
  };

  const hasAnyRole = (roles: PermissionType[]): boolean => {
    return roles.some(role => currentUser?.roles?.includes(role));
  };


  return (
    <Sheet
      className="Sidebar"
      sx={{
        position: { xs: 'fixed', md: 'sticky' },
        transform: {
          xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1)))',
          md: 'none',
        },
        transition: 'transform 0.4s, width 0.4s',
        zIndex: 10000,
        height: '100dvh',
        width: 'var(--Sidebar-width)',
        top: 0,
        p: 2,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        borderRight: '1px solid',
        borderColor: 'divider',
      }}
    >
      <GlobalStyles
        styles={(theme) => ({
          ':root': {
            '--Sidebar-width': '225px',
            [theme.breakpoints.up('lg')]: {
              '--Sidebar-width': '245px',
            },
          },
        })}
      />
      <Box
        className="Sidebar-overlay"
        sx={{
          position: 'fixed',
          zIndex: 9998,
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          opacity: 'var(--SideNavigation-slideIn)',
          backgroundColor: 'var(--joy-palette-background-backdrop)',
          transition: 'opacity 0.4s',
          transform: {
            xs: 'translateX(calc(100% * (var(--SideNavigation-slideIn, 0) - 1) + var(--SideNavigation-slideIn, 0) * var(--Sidebar-width, 0px)))',
            lg: 'translateX(-100%)',
          },
        }}
        onClick={() => closeSidebar()}
      />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center'}}>
        <Box
          component="img"
          sx={{ display: { xs: 'none', md: 'flex' }, m: 1, height: 25 }}
          alt="F&G TECHNOLOGIES"
          src={logo} />
        <Typography level="title-lg" sx={{fontSize: 15, fontWeight: 700}}>F&G TECHNOLOGIES</Typography>
      </Box>
      <Box
        sx={{
          minHeight: 0,
          overflow: 'hidden auto',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          [`& .${listItemButtonClasses.root}`]: {
            gap: 1.5,
          },
        }}
      >
        <List
          size="sm"
          sx={{
            gap: 1,
            '--List-nestedInsetStart': '30px',
            '--ListItem-radius': (theme) => theme.vars.radius.sm,
          }}
        >

          {hasRole(PermissionType.MY_CASH_BOXES) && (
            <ListItem>
              <ListItemButton
                role="menuitem"
                component={NavLink}
                to="/my-cash-boxes"
                sx={{
                  '&.active': {
                    backgroundColor: "#DDE7EE",
                    fontWeight: 600
                  }
                }}
              >
                <LocalAtmIcon />
                <ListItemContent>
                  <Typography level="title-sm">Mis Cajas</Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          )}

          {hasRole(PermissionType.MY_SERVICE_ORDERS) && (
            <ListItem>
              <ListItemButton
                role="menuitem"
                component={NavLink}
                to="/my-service-orders"
                sx={{
                  '&.active': {
                    backgroundColor: "#DDE7EE",
                    fontWeight: 600
                  }
                }}
              >
                <DesignServicesIcon />
                <ListItemContent>
                  <Typography level="title-sm">Mis Ordenes de Servicio</Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          )}

          {hasRole(PermissionType.MY_LIQUIDATIONS) && (
            <ListItem>
              <ListItemButton
                role="menuitem"
                component={NavLink}
                to="/my-liquidations"
                sx={{
                  '&.active': {
                    backgroundColor: "#DDE7EE",
                    fontWeight: 600
                  }
                }}
              >
                <CurrencyExchangeIcon />
                <ListItemContent>
                  <Typography level="title-sm">Mis Liquidaciones</Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          )}

          {hasRole(PermissionType.MY_EVALUATIONS) && (
            <ListItem>
              <ListItemButton
                role="menuitem"
                component={NavLink}
                to="/my-evaluations"
                sx={{
                  '&.active': {
                    backgroundColor: "#DDE7EE",
                    fontWeight: 600
                  }
                }}
              >
                <GradingIcon />
                <ListItemContent>
                  <Typography level="title-sm">Mis Evaluaciones</Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          )}

          {hasAnyRole([PermissionType.CASH_BOXES, PermissionType.SALES, PermissionType.PURCHASES, PermissionType.LIQUIDATIONS]) && (
            <ListItem nested>
              <Toggler
                defaultExpanded={getOpen([
                  'sales',
                  'purchases'
                ])}
                renderToggle={({ open, setOpen }) => (
                  <ListItemButton onClick={() => setOpen(!open)}>
                    <AssuredWorkloadIcon />
                    <ListItemContent>
                      <Typography level="title-sm">Control Financiero</Typography>
                    </ListItemContent>
                    <KeyboardArrowDownIcon
                      sx={[
                        open
                          ? {
                            transform: 'rotate(180deg)',
                          }
                          : {
                            transform: 'none',
                          },
                      ]}
                    />
                  </ListItemButton>
                )}
              >
                <List sx={{ gap: 0.5 }}>
                  {hasRole(PermissionType.CASH_BOXES) && (
                    <ListItem sx={{ mt: 0.5 }}>
                      <ListItemButton
                        role="menuitem"
                        component={NavLink}
                        to="/cash-boxes"
                        sx={{
                          '&.active': {
                            backgroundColor: "#DDE7EE",
                            fontWeight: 600
                          }
                        }}
                      >
                        <LocalAtmIcon />
                        <ListItemContent>
                          <Typography level="title-sm">Cajas</Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  )}

                  {hasRole(PermissionType.SALES) && (
                    <ListItem sx={{ mt: 0.5 }}>
                      <ListItemButton
                        role="menuitem"
                        component={NavLink}
                        to="/sales"
                        sx={{
                          '&.active': {
                            backgroundColor: "#DDE7EE",
                            fontWeight: 600
                          }
                        }}
                      >
                        <PointOfSaleIcon />
                        <ListItemContent>
                          <Typography level="title-sm">Ventas</Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  )}

                  {hasRole(PermissionType.PURCHASES) && (
                    <ListItem sx={{ mt: 0.5 }}>
                      <ListItemButton
                        role="menuitem"
                        component={NavLink}
                        to="/purchases"
                        sx={{
                          '&.active': {
                            backgroundColor: "#DDE7EE",
                            fontWeight: 600
                          }
                        }}
                      >
                        <ShoppingCartIcon />
                        <ListItemContent>
                          <Typography level="title-sm">Compras</Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  )}

                  {hasRole(PermissionType.LIQUIDATIONS) && (
                    <ListItem sx={{ mt: 0.5 }}>
                      <ListItemButton
                        role="menuitem"
                        component={NavLink}
                        to="/liquidations"
                        sx={{
                          '&.active': {
                            backgroundColor: "#DDE7EE",
                            fontWeight: 600
                          }
                        }}
                      >
                        <CurrencyExchangeIcon />
                        <ListItemContent>
                          <Typography level="title-sm">Liquidaciones</Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  )}

                </List>
              </Toggler>
            </ListItem>
          )}

          {hasRole(PermissionType.SERVICE_ORDERS) && (
            <ListItem>
              <ListItemButton
                role="menuitem"
                component={NavLink}
                to="/service-orders"
                sx={{
                  '&.active': {
                    backgroundColor: "#DDE7EE",
                    fontWeight: 600
                  }
                }}
              >
                <DesignServicesIcon />
                <ListItemContent>
                  <Typography level="title-sm">Ordenes de Servicio</Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          )}

          {hasRole(PermissionType.PROVIDERS) && (
            <ListItem>
              <ListItemButton
                role="menuitem"
                component={NavLink}
                to="/providers"
                sx={{
                  '&.active': {
                    backgroundColor: "#DDE7EE",
                    fontWeight: 600
                  }
                }}
              >
                <BadgeIcon/>
                <ListItemContent>
                  <Typography level="title-sm">Proveedores</Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          )}

          {hasRole(PermissionType.CLIENTS) && (
            <ListItem>
              <ListItemButton
                role="menuitem"
                component={NavLink}
                to="/clients"
                sx={{
                  '&.active': {
                    backgroundColor: "#DDE7EE",
                    fontWeight: 600
                  }
                }}
              >
                <SwitchAccountIcon />
                <ListItemContent>
                  <Typography level="title-sm">Clientes</Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          )}

          {hasAnyRole([PermissionType.ACCOUNTS, PermissionType.CATEGORIES, PermissionType.TECHNIQUES]) && (
            <ListItem nested>
              <Toggler
                defaultExpanded={getOpen([
                  'sales',
                  'purchases'
                ])}
                renderToggle={({ open, setOpen }) => (
                  <ListItemButton onClick={() => setOpen(!open)}>
                    <SettingsIcon />
                    <ListItemContent>
                      <Typography level="title-sm">Configuraciones</Typography>
                    </ListItemContent>
                    <KeyboardArrowDownIcon
                      sx={[
                        open
                          ? {
                            transform: 'rotate(180deg)',
                          }
                          : {
                            transform: 'none',
                          },
                      ]}
                    />
                  </ListItemButton>
                )}
              >
                <List sx={{ gap: 0.5 }}>
                  {hasRole(PermissionType.ACCOUNTS) && (
                    <ListItem sx={{ mt: 0.5 }}>
                      <ListItemButton
                        role="menuitem"
                        component={NavLink}
                        to="/accounts"
                        sx={{
                          '&.active': {
                            backgroundColor: "#DDE7EE",
                            fontWeight: 600
                          }
                        }}
                      >
                        <AccountBalanceWalletIcon />
                        <ListItemContent>
                          <Typography level="title-sm">Cuentas</Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  )}

                  {hasRole(PermissionType.CATEGORIES) && (
                    <ListItem sx={{ mt: 0.5 }}>
                      <ListItemButton
                        role="menuitem"
                        component={NavLink}
                        to="/categories"
                        sx={{
                          '&.active': {
                            backgroundColor: "#DDE7EE",
                            fontWeight: 600
                          }
                        }}
                      >
                        <CategoryIcon />
                        <ListItemContent>
                          <Typography level="title-sm">Categorias</Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  )}

                  {hasRole(PermissionType.TECHNIQUES) && (
                    <ListItem sx={{ mt: 0.5 }}>
                      <ListItemButton
                        role="menuitem"
                        component={NavLink}
                        to="/techniques"
                        sx={{
                          '&.active': {
                            backgroundColor: "#DDE7EE",
                            fontWeight: 600
                          }
                        }}
                      >
                        <HandymanIcon />
                        <ListItemContent>
                          <Typography level="title-sm">Tecnicas</Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  )}

                  {hasRole(PermissionType.REVISION_ITEMS) && (
                    <ListItem sx={{ mt: 0.5 }}>
                      <ListItemButton
                        role="menuitem"
                        component={NavLink}
                        to="/revision-items"
                        sx={{
                          '&.active': {
                            backgroundColor: "#DDE7EE",
                            fontWeight: 600
                          }
                        }}
                      >
                        <AccountTreeIcon />
                        <ListItemContent>
                          <Typography level="title-sm">Items de Revision</Typography>
                        </ListItemContent>
                      </ListItemButton>
                    </ListItem>
                  )}

                </List>
              </Toggler>
            </ListItem>
          )}

          {hasRole(PermissionType.USERS_AND_PROFILES) && (
            <ListItem>
            <ListItemButton
              role="menuitem"
              component={NavLink}
              to="/users"
              sx={{
                '&.active': {
                  backgroundColor: "#DDE7EE",
                  fontWeight: 600
                }
              }}
            >
              <ManageAccountsIcon />
              <ListItemContent>
                <Typography level="title-sm">Usuarios y Legajos</Typography>
              </ListItemContent>
            </ListItemButton>
          </ListItem>
          )}

          {hasRole(PermissionType.ROLES_AND_PERMISSIONS) && (
            <ListItem>
              <ListItemButton
                role="menuitem"
                component={NavLink}
                to="/roles"
                sx={{
                  '&.active': {
                    backgroundColor: "#DDE7EE",
                    fontWeight: 600
                  }
                }}
              >
                <AdminPanelSettingsIcon />
                <ListItemContent>
                  <Typography level="title-sm">Roles y Permisos</Typography>
                </ListItemContent>
              </ListItemButton>
            </ListItem>
          )}

        </List>
        <List
          size="sm"
          sx={{
            mt: 'auto',
            flexGrow: 0,
            '--ListItem-radius': (theme) => theme.vars.radius.sm,
            '--List-gap': '8px',
            mb: 2,
          }}
        >
          <ListItem>
            <ListItemButton
              role="menuitem"
              component={NavLink}
              to="/profile"
              sx={{
                '&.active': {
                  backgroundColor: "#DDE7EE",
                  fontWeight: 600
                }
              }}
            >
              <AccountBoxIcon />
              Perfil
            </ListItemButton>
          </ListItem>
        </List>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Avatar
          src={currentUser?.profileImage ? currentUser.profileImage : "/static/images/avatar/1.jpg"}
          variant="outlined"
          size="sm"
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography level="title-sm">{currentUser?.username}</Typography>
          {/*<Typography level="body-xs">{currentUser?.email}</Typography>*/}
        </Box>
        <IconButton size="sm" variant="plain" color="neutral">
          <LogoutRoundedIcon onClick={() => logOut()} />
        </IconButton>
      </Box>
    </Sheet>
  );
}
